import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import './ContactForm.css';

function ContactForm() {
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        email: '',
        message: '',
    });

    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        setError('');
        setSuccess('');
    };

    const validateForm = () => {
        if (!formData.name.trim()) {
            setError('Please enter your name.');
            return false;
        }
        if (!formData.phone.trim() && !formData.email.trim()) {
            setError('Please provide a phone number or email.');
            return false;
        }
        if (!formData.message.trim()) {
            setError('Please enter your inquiry.');
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        setError('');
        setSuccess('');

        if (!validateForm()) return;

        setIsSubmitting(true);

        try {
            const response = await fetch('https://api.cleansweep.ae/send-inquiry', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData),
            });

            if (response.ok) {
                setSuccess('Inquiry sent successfully! We will contact you shortly.');
                setFormData({ name: '', phone: '', email: '', message: '' });
            } else {
                setError('Failed to send inquiry. Please try again.');
            }
        } catch (err) {
            setError('Network error. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // ✅ WhatsApp link with pre-filled message (includes their name if provided)
    const whatsappPhone = '971508364814';
    const whatsappText = encodeURIComponent(
        `Hi CleanSweep, I would like a quote for waste collection in Dubai.\n\nName: ${formData.name || '-'}\nPhone: ${formData.phone || '-'}\nEmail: ${formData.email || '-'}\nMessage: ${formData.message || '-'}`
    );
    const whatsappLink = `https://wa.me/${whatsappPhone}?text=${whatsappText}`;

    return (
        <section className="contact-us-section" id="contact-us">
            <div className="contact-container">
                {/* Left Side */}
                <div className="contact-info">
                    <span className="subtitle">Get In Touch</span>
                    <h2>Contact CleanSweep Dubai</h2>
                    <p>
                        Ready for reliable service for a cleaner environment? Our team is
                        available 24/7 for your waste management needs.
                    </p>

                    <div className="info-item">
                        <div className="icon-box">
                            <Phone size={20} />
                        </div>
                        <div>
                            <h4>Call Us</h4>
                            <a href="tel:+971508364814">+971 50 836 4814</a>
                        </div>
                    </div>

                    <div className="info-item">
                        <div className="icon-box">
                            <Mail size={20} />
                        </div>
                        <div>
                            <h4>Email Us</h4>
                            <a href="mailto:info@cleansweep.ae">info@cleansweep.ae</a>
                        </div>
                    </div>

                    <div className="info-item">
                        <div className="icon-box">
                            <MapPin size={20} />
                        </div>
                        <div>
                            <h4>Location</h4>
                            <p>Dubai, United Arab Emirates</p>
                        </div>
                    </div>
                </div>

                {/* Right Side: Form */}
                <div className="contact-form-card">
                    <form onSubmit={handleSubmit}>
                        <input
                            type="text"
                            name="name"
                            placeholder="Your Name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                        />

                        <div className="form-row">
                            <input
                                type="text"
                                name="phone"
                                placeholder="Phone Number"
                                value={formData.phone}
                                onChange={handleChange}
                            />
                            <input
                                type="email"
                                name="email"
                                placeholder="Email Address"
                                value={formData.email}
                                onChange={handleChange}
                            />
                        </div>

                        <textarea
                            name="message"
                            placeholder="How can we help you?"
                            value={formData.message}
                            onChange={handleChange}
                            required
                        />

                        <button
                            type="submit"
                            className="submit-btn"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                'Sending...'
                            ) : (
                                <>
                                    Send Inquiry <Send size={18} />
                                </>
                            )}
                        </button>

                        {/* ✅ WhatsApp Button inside form */}
                        <a
                            className="whatsapp-form-btn"
                            href={whatsappLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Chat with us on WhatsApp"
                        >
                            <img
                                src="/whatsapp.png"
                                alt="WhatsApp"
                                className="whatsapp-form-icon"
                            />
                            Chat on WhatsApp
                        </a>
                    </form>

                    {error && <p className="form-error">{error}</p>}
                    {success && <p className="form-success">{success}</p>}
                </div>
            </div>
        </section>
    );
}

export default ContactForm;
